<script setup>

import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useCart } from '@/frontend/vue/composables/useCart'

const props = defineProps({
    batch: {
        type: Object,
        required: true,
    },
    isBrochureMode: {
        type: [Boolean, String],
        default: false
    },
    showPrices: {
        type: [Boolean, String],
        default: true
    },
    showBotanical: {
        type: [Boolean, String],
        default: false
    },
    defaultImage: {
        type: String,
        default: '/images/default.jpg'
    }
})

// Convert string props to boolean
const isBrochureMode = computed(() => {
    return props.isBrochureMode === true || props.isBrochureMode === 'true'
})

const showPrices = computed(() => {
    return props.showPrices === true || props.showPrices === 'true'
})

const shouldShowBotanicalName = computed(() => {
    const isEnabled = props.showBotanical === 'true' || props.showBotanical === true
    const displayName = props.batch?.display_html || props.batch?.html
    const botanicalName = props.batch?.html
    const namesDiffer = displayName !== botanicalName
    return isEnabled && namesDiffer && botanicalName
})

const { cartItems, updateQuantity, formatPrice } = useCart()

// Find existing cart line for this batch
const line = computed(() =>
    cartItems.value.find(i => i.id === props.batch.id && i.type === 'batch') || null
)

const max_quantity = computed(() => props.batch.effective_quantity || 999)
const can_add = computed(() => scoped_qty.value > 0)
const can_increase = computed(() => scoped_qty.value < max_quantity.value)

const scoped_qty = ref(line.value?.quantity || 0)

watch(
    line,
    newLine => {
        scoped_qty.value = newLine?.quantity || 0
    },
    { immediate: true }
)

function handleCart() {
    const qty = parseInt(scoped_qty.value, 10)
    updateQuantity({
        id:       props.batch.id,
        handle:   props.batch.metadata.product.handle,
        type:     'batch',
        name:     `${props.batch.display_name || props.batch.title} - ${props.batch.variant}`,
        price:    props.batch.special_price,
        quantity: qty,
        variant:  props.batch.variant,
        code:     props.batch.sku,
        image:    props.batch.image || props.defaultImage,
    })
}

// Add event listener for apply-cart event
const handleApplyCart = () => {
    if (can_add.value) {
        handleCart();
    }
};

onMounted(() => {
    window.addEventListener('apply-cart', handleApplyCart);
});

onUnmounted(() => {
    window.removeEventListener('apply-cart', handleApplyCart);
});
</script>

<template>
    <div class="grid__item product-card">
        <figure class="product-card__image-area">
            <img :src="props.batch.image || props.defaultImage" alt="" />
        </figure>
        <div class="product-card__badges">
            <span class="pill pill-danger">Sale</span>
        </div>
        <div class="product-card__content-full">
            <div class="product-card__content-full__text-area">
                <div class="product-card__content-full__text-area__text">
                    <h3 class="product-card__title" v-html="props.batch.display_html || props.batch.html"></h3>
                    <div class="product-card__botanical-name" v-if="shouldShowBotanicalName" v-html="props.batch.html"></div>
                    <h4 class="product-card__subtitle">{{ props.batch.variant }}</h4>
                    <h5 class="product-card__footnote">{{ props.batch.sku }}</h5>
                </div>
                <div v-if="showPrices" class="product-card__content-full__text-area__price">
                    <div class="product-card__price before-price">
                        {{ formatPrice(props.batch.retail_price) }}
                    </div>
                    <div class="product-card__price">
                        {{ formatPrice(props.batch.special_price) }}
                    </div>
                </div>
            </div>
            <div v-if="!isBrochureMode" class="product-card__content-full__control-area">
                <div class="quantity-control">
                    <button type="button" class="number-decrease" @click="scoped_qty = Math.max(0, scoped_qty - 1)">-</button>
                    <input
                        type="number"
                        min="0"
                        :max="max_quantity"
                        v-model.number="scoped_qty"
                        @input="scoped_qty = Math.min(Math.max(0, scoped_qty), max_quantity)"
                    />
                    <button 
                        type="button" 
                        class="number-increase" 
                        :disabled="!can_increase"
                        @click="scoped_qty = Math.min(scoped_qty + 1, max_quantity)"
                    >+</button>
                </div>
                <div>
                    <button :disabled="!can_add" class="btn btn-primary btn-sm" @click="handleCart">{{ line ? 'Update Cart' : 'Add to Cart' }}</button>
                </div>
            </div>
        </div>
    </div>
</template>
